/* eslint-disable @typescript-eslint/no-empty-function */
import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Params } from '@angular/router';
import { Actions } from '@ngrx/effects';
import { AuthenticationStateService } from '@quorum/authentication/services';
import { GMServiceVisitByDealerQueryParameters, GMServiceVisitByVinQueryParameters } from '@quorum/models/oem-query';
import {
  GMServiceVisitByDealer,
  GMServiceVisitByVin,
  GMServiceVisitDtc,
  GMServiceVisitDtcCount,
  GMServiceVisitVehicleData,
} from '@quorum/models/oem-resource';
import { AuthenticatedUser, Employee } from '@quorum/models/xs-resource';
import { OemMenuService } from '@quorum/quorum-api';
import { ServiceLaneDropdownsFacade } from '@quorum/service-lane-dropdowns/facade';
import { status, SvcVehicleDiagnosticsRootFacade } from '@quorum/svc-vehicle-diagnostics-root';
import { combineLatest, Observable, of, Subject } from 'rxjs';
import { filter, map, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { GmVehicleDiagnosticsFilteringDialogComponent } from '../gm-vehicle-diagnostics-filtering-dialog/gm-vehicle-diagnostics-filtering-dialog.component';
import { BrowserControlCommunicationService } from '@applications/sha-services';
import { ServiceLaneFacade } from '@quorum/service-lane-root/facade';

@Component({
  selector: 'quorum-gm-vehicle-diagnostics',
  templateUrl: './gm-vehicle-diagnostics.component.html',
  styleUrls: ['./gm-vehicle-diagnostics.component.scss'],
})
export class GmVehicleDiagnosticsComponent implements OnInit, OnDestroy {
  vehicleDiagnosticsData$!: Observable<{
    routerParams: Params;
    businessId: string;
    authenticatedUser: AuthenticatedUser;
    authenticatedEmployee: Employee;
  }>;

  linkedServiceData$: Observable<{
    linkedServiceVisit: GMServiceVisitByVin | GMServiceVisitByDealer | null;
    serviceVisitDTCs: GMServiceVisitDtc[] | null;
    serviceVisitMPVIs: GMServiceVisitVehicleData[] | null;
    serviceVisitECUs: GMServiceVisitDtcCount[] | null;
  }>;

  openTab = 0;
  destroy$ = new Subject();

  dataLoading$: Observable<boolean>;
  postedRO = false;
  constructor(
    private activatedRoute: ActivatedRoute,
    public dialog: MatDialog,
    private serviceLaneDropdownFacade: ServiceLaneDropdownsFacade,
    private serviceLaneFacade: ServiceLaneFacade,
    public svcVehicleDiagnosticsRootFacade: SvcVehicleDiagnosticsRootFacade,
    private oemMenuService: OemMenuService,
    private authenticationStateService: AuthenticationStateService,
    public actions$: Actions,
    public browserControlService: BrowserControlCommunicationService
  ) {}

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnInit(): void {
    this.linkedServiceData$ = combineLatest([
      this.svcVehicleDiagnosticsRootFacade.linkedServiceVisit$,
      this.svcVehicleDiagnosticsRootFacade.linkedServiceVisitDtcs$,
      this.svcVehicleDiagnosticsRootFacade.linkedServiceVisitMpvi$,
      this.svcVehicleDiagnosticsRootFacade.linkedServiceVisitEcus$,
    ]).pipe(
      map(([linkedServiceVisit, linkedServiceVisitDtcs, linkedServiceVisitMpvi, linkedServiceVisitEcus]) => {
        return {
          linkedServiceVisit: linkedServiceVisit,
          serviceVisitDTCs: linkedServiceVisitDtcs,
          serviceVisitMPVIs: linkedServiceVisitMpvi,
          serviceVisitECUs: linkedServiceVisitEcus,
        };
      })
    );

    this.dataLoading$ = combineLatest([
      this.svcVehicleDiagnosticsRootFacade.serviceVisitsByVinStatus$,
      this.svcVehicleDiagnosticsRootFacade.linkedServiceVisitDtcsStatus$,
      this.svcVehicleDiagnosticsRootFacade.linkedServiceVisitMpviStatus$,
      this.svcVehicleDiagnosticsRootFacade.linkedServiceVisitEcusStatus$,
    ]).pipe(
      map(([serviceVisitsByVinStatus, serviceVisitDtcsStatus, serviceVisitMpviStatus, serviceVisitEcusStatus]) => {
        if (
          [serviceVisitsByVinStatus, serviceVisitDtcsStatus, serviceVisitMpviStatus, serviceVisitEcusStatus].some(
            (value) => value === 'loading'
          )
        ) {
          return true;
        } else {
          return false;
        }
      })
    );

    this.vehicleDiagnosticsData$ = combineLatest([
      this.authenticationStateService.selectAuthenticatedUser().pipe(
        filter((authenticatedUser) => authenticatedUser !== null),
        tap((authenticatedUser) => {
          this.oemMenuService.initialize(authenticatedUser.user.destination);
        })
      ),
      this.authenticationStateService.selectAuthenticatedEmployee().pipe(
        filter((employee) => employee !== null),
        take(1)
      ),
      this.oemMenuService.businessId$.pipe(
        filter((businessId) => businessId != null),
        take(1)
      ),
      this.activatedRoute.queryParams.pipe(
        switchMap((params) => {
          if (params['transactionId'] && params['transactionType'] === 'workOrder') {
            return this.svcVehicleDiagnosticsRootFacade
              .getTransaction(
                params['transactionId'],
                params['transactionType'],
                this.browserControlService.embeddedInXsellerator
              )
              .pipe(
                map((workOrder) => {
                  if (workOrder.statusId === '0') {
                    this.postedRO = true;
                  }
                  return params;
                })
              );
          } else {
            return of(params);
          }
        })
      ),
      this.svcVehicleDiagnosticsRootFacade.linkedServiceVisit$.pipe(take(1)),
      this.svcVehicleDiagnosticsRootFacade.dealerBac$,
    ]).pipe(
      switchMap(
        ([authenticatedUser, authenticatedEmployee, businessId, routerParams, linkedServiceVisit, dealerBac]) => {
          // if there is a transactionId, we came from XS and must get savi record using the vin, businessId and dealerCountryCode
          if (routerParams['transactionId']) {
            const oneYearAgo = new Date();
            oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
            oneYearAgo.setHours(0, 0, 0);

            const today = new Date();
            today.setHours(23, 59, 59);
            const fromXsellerator = true;

            if (this.serviceLaneFacade.isLeapYear(today)) {
              oneYearAgo.setDate(oneYearAgo.getDate() + 1);
            }

            this.svcVehicleDiagnosticsRootFacade
              .gmActionListener(
                businessId,
                routerParams['dealerCountryCode'],
                routerParams['vin'],
                routerParams['transactionId'],
                routerParams['transactionType'],
                fromXsellerator
              )
              .pipe(takeUntil(this.destroy$))
              .subscribe();

            this.svcVehicleDiagnosticsRootFacade.getGMServiceVisitByVin(
              businessId,
              routerParams['dealerCountryCode'],
              routerParams['vin'],
              // used to default to todays date
              new GMServiceVisitByVinQueryParameters({
                startDate: oneYearAgo.toISOString(),
                endDate: today.toISOString(),
                bac: dealerBac,
                destinationId: authenticatedUser.user.destination,
              })
            );

            this.svcVehicleDiagnosticsRootFacade.getGMServiceVisitByDealer(
              businessId,
              routerParams['dealerCountryCode'],
              oneYearAgo.toISOString(),
              today.toISOString(),
              dealerBac,
              new GMServiceVisitByDealerQueryParameters({
                destinationId: authenticatedUser.user.destination,
              })
            );

            combineLatest([
              this.svcVehicleDiagnosticsRootFacade.serviceVisitsByVinStatus$.pipe(
                filter((vinStatus: status) => vinStatus === status.succeeded)
              ),
              this.svcVehicleDiagnosticsRootFacade.serviceVisitsByVin$.pipe(
                filter((serviceVisit) => serviceVisit.length > 0)
              ),
              this.svcVehicleDiagnosticsRootFacade.getAlreadyLinkedServiceVisitIds$,
            ])
              .pipe(
                take(1),
                tap(([serviceVistByVinStateStatus, serviceVisitsByVin, alreadyLinkedServiceVisitIds]) => {
                  if (routerParams['serviceVisitId'] == undefined) {
                    const mostRecentServiceVisit: GMServiceVisitByVin | GMServiceVisitByDealer =
                      this.svcVehicleDiagnosticsRootFacade.getLatestServiceVisit(serviceVisitsByVin);
                    // only automatically link if the most recent visit is within last 24 hours and not already linked
                    if (
                      this.svcVehicleDiagnosticsRootFacade.isWithinADay(
                        mostRecentServiceVisit.checkInDate,
                        mostRecentServiceVisit.checkInTime
                      ) &&
                      !alreadyLinkedServiceVisitIds.includes(mostRecentServiceVisit.serviceVisitId)
                    ) {
                      this.svcVehicleDiagnosticsRootFacade.updateLinkedServiceVisitInState(
                        mostRecentServiceVisit.serviceVisitId
                      );
                    }
                  } else {
                    this.svcVehicleDiagnosticsRootFacade.updateLinkedServiceVisitInState(
                      routerParams['serviceVisitId']
                    );
                  }
                })
              )
              .subscribe();
          } else {
            // we're in PL and can manually retry to get newest service visit
          }
          return of({
            routerParams: routerParams,
            businessId: businessId,
            authenticatedUser: authenticatedUser,
            authenticatedEmployee: authenticatedEmployee,
          });
        }
      ),
      tap((vehicleDiagnosticsData) => console.log(vehicleDiagnosticsData))
    );
  }

  unlinkSaviRecord() {
    this.svcVehicleDiagnosticsRootFacade.clearLinkedServiceVisitState();
  }

  openGMSaviFilteringDialog(vin: string, dealerCountryCode: string, businessId: string) {
    const dialogRef = this.dialog.open(GmVehicleDiagnosticsFilteringDialogComponent, {
      disableClose: true,
      width: '815px',
      height: '720px',
      autoFocus: false,
      data: {
        serviceAdvisors$: this.serviceLaneDropdownFacade.serviceAdvisors$,
        vin: vin,
        dealerCountryCode: dealerCountryCode,
        businessId: businessId,
      },
    });

    dialogRef.afterClosed().subscribe((selectedSaviRecord: GMServiceVisitByVin | GMServiceVisitByDealer) => {
      if (selectedSaviRecord) {
        this.svcVehicleDiagnosticsRootFacade.updateLinkedServiceVisitInState(selectedSaviRecord.serviceVisitId);
      }
    });
  }

  retrySaviRequests(
    vin: string,
    dealerCountryCode: string,
    businessId: string,
    dealerBac: string,
    authenticatedUser: AuthenticatedUser
  ) {
    const oneYearAgo = new Date();
    oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
    oneYearAgo.setHours(0, 0, 0);

    const today = new Date();
    today.setHours(23, 59, 59);

    if (this.serviceLaneFacade.isLeapYear(today)) {
      oneYearAgo.setDate(oneYearAgo.getDate() + 1);
    }

    this.svcVehicleDiagnosticsRootFacade.getGMServiceVisitByVin(
      businessId,
      dealerCountryCode,
      vin,
      new GMServiceVisitByVinQueryParameters({
        startDate: oneYearAgo.toISOString(),
        endDate: today.toISOString(),
        bac: dealerBac,
        destinationId: authenticatedUser.user.destination,
      })
    );

    this.svcVehicleDiagnosticsRootFacade.getGMServiceVisitByDealer(
      businessId,
      dealerCountryCode,
      oneYearAgo.toISOString(),
      today.toISOString(),
      dealerBac,
      new GMServiceVisitByDealerQueryParameters({ destinationId: authenticatedUser.user.destination })
    );
    combineLatest([
      this.svcVehicleDiagnosticsRootFacade.serviceVisitsByVinStatus$.pipe(
        filter((vinStatus: status) => vinStatus === status.succeeded)
      ),
      this.svcVehicleDiagnosticsRootFacade.serviceVisitsByVin$.pipe(
        filter((serviceVisit) => serviceVisit.length > 0),
        switchMap((serviceVisitsByVin) => {
          return this.svcVehicleDiagnosticsRootFacade.linkedServiceVisit$.pipe(
            map((linkedServiceVisitFromState) => {
              return { serviceVisitsByVin, linkedServiceVisitFromState };
            })
          );
        })
      ),
      this.svcVehicleDiagnosticsRootFacade.getAlreadyLinkedServiceVisitIds$,
    ])
      .pipe(
        take(1),
        tap(([serviceVistByVinStateStatus, serviceVisitsData, alreadyLinkedServiceVisitIds]) => {
          if (
            serviceVisitsData.linkedServiceVisitFromState == undefined ||
            serviceVisitsData.linkedServiceVisitFromState.serviceVisitId === null
          ) {
            const mostRecentServiceVisit: GMServiceVisitByVin | GMServiceVisitByDealer =
              this.svcVehicleDiagnosticsRootFacade.getLatestServiceVisit(serviceVisitsData.serviceVisitsByVin);
            // only automatically link if the most recent visit is within last 24 hours and not already linked
            if (
              this.svcVehicleDiagnosticsRootFacade.isWithinADay(
                mostRecentServiceVisit.checkInDate,
                mostRecentServiceVisit.checkInTime
              ) &&
              !alreadyLinkedServiceVisitIds.includes(mostRecentServiceVisit.serviceVisitId)
            ) {
              this.svcVehicleDiagnosticsRootFacade.updateLinkedServiceVisitInState(
                mostRecentServiceVisit.serviceVisitId
              );
            }
          } else {
            this.svcVehicleDiagnosticsRootFacade.updateLinkedServiceVisitInState(
              serviceVisitsData.linkedServiceVisitFromState.serviceVisitId
            );
          }
        })
      )
      .subscribe();
  }

  toggleTabs(tabNumber: number) {
    this.openTab = tabNumber;
  }
}
